import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useRedirect } from "hooks/useRedirect";

import type { ContentAdapterAPI } from "generated/types";

import { useOnClickOutside } from "hooks/useOnClickOutside";
import { useState } from "react";
import { CssIcon, CssLink } from "styles/helpers/layout";
import {
  StyledArrowIcon,
  StyledTheoryButton,
  StyledTheoryContainer,
  StyledTheoryList,
} from "./EdifyTheoryButton.styled";

interface EdifyTheoryButtonProps {
  theoryTopics: ContentAdapterAPI.EdifyTopicListItem[];
  buildEdifyTopicDetailsUrl: (topicId: string) => string;
}

export const EdifyTheoryButton: React.FC<EdifyTheoryButtonProps> = ({
  theoryTopics,
  buildEdifyTopicDetailsUrl,
}) => {
  const { t } = useTranslation("homepage");
  const { redirectToUrl } = useRedirect();

  const [isListHidden, hideList] = useState(true);
  const theoryContainerRef = React.useRef<HTMLDivElement>(null);

  // When clicking outside of theory list container, automatically hide list
  useOnClickOutside({
    ref: theoryContainerRef,
    handler: useCallback(() => isListHidden && hideList(true), []),
  });

  if (!theoryTopics.length) {
    return null;
  }

  // If there's only one theory block available, button will redirect to it
  if (theoryTopics.length === 1) {
    const [topic] = theoryTopics;
    return (
      <StyledTheoryButton
        data-cy="view-edify-theory-button"
        onClick={() => redirectToUrl(buildEdifyTopicDetailsUrl(topic.id))}
      >
        <CssIcon iconName="fad fa-book-open" />
        {t("edifySubjectDetails.topicListItem.viewTheory")}
        <StyledArrowIcon fixedWidth iconName="fas fa-caret-right" />
      </StyledTheoryButton>
    );
  }

  // If multiple theory blocks are available, button will toggle visibility of a list with
  // links to the different theory topics
  return (
    <StyledTheoryContainer ref={theoryContainerRef}>
      <StyledTheoryButton
        data-cy="toggle-edify-theory-topic-list-button"
        onClick={() => hideList(!isListHidden)}
      >
        <CssIcon iconName="fad fa-book-open" />
        {t("edifySubjectDetails.topicListItem.viewTheory")}
        <StyledArrowIcon
          fixedWidth
          iconName={isListHidden ? "fas fa-caret-down" : "fas fa-caret-up"}
        />
      </StyledTheoryButton>
      <StyledTheoryList data-cy="edify-theory-topic-list" $isHidden={isListHidden}>
        {theoryTopics.map((topic) => (
          <li key={topic.id} data-cy="edify-theory-topic-list-item">
            <CssLink to={buildEdifyTopicDetailsUrl(topic.id)}>{topic.title}</CssLink>
          </li>
        ))}
      </StyledTheoryList>
    </StyledTheoryContainer>
  );
};
